import {RequestEntity} from "../../../../types/RequestEntity";
import {isCombinedRequest} from "../../../../lib/isCombinedRequest";
import {isSingleRequest} from "../../../../lib/isSingleRequest";
import {
    CombinedEquipmentRequestEntity,
    EquipmentRequestEntity,
    EquipmentRequestStatusEnum,
    useEquipmentRequestCreateHistoryRecordMutation,
    useUpdateCombinedEquipmentRequestMutation,
    useUpdateEquipmentRequestMutation,
    useUpdateEquipmentRequestsStatusesMutation
} from "graphql/graphQlApiHooks";
import React, {useEffect, useState} from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from "@mui/material";
import StatusSelect from "../../../StatusSelect";
import {useSuppliedRequestAction} from "../../../../hooks/actions/useSuppliedRequestAction";
import {getLabelStatusRequest} from "../../../../lib/getLabelStatusRequest";
import {useRejectRequestAction} from "../../../../hooks/actions/useRejectRequestAction";
import {LoadingButton} from "@mui/lab";
import {CombinedEquipmentAcceptanceDialog} from "../../../Dialogs/CombinedEquipmentAcceptanceDialog";
import {SingleEquipmentAcceptanceDialog} from "../../../Dialogs/SingleEquipmentAcceptanceDialog";
import {useDialog} from "providers/DialogProvider";
import { useSnackbar } from "notistack";

export const RequestStatusCellRenderer = ({equipmentRequestEntity, disableEdit}: {
    equipmentRequestEntity: RequestEntity,
    disableEdit?: boolean
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading ] = useState(false);
    const [status, setStatus] = useState(equipmentRequestEntity?.requestStatus);
    const {onSuppliedDialogHandler} = useSuppliedRequestAction();
    const [openWarning, setOpenWarning] = useState(false);
    const [nextStatus, setNextStatus] = useState<EquipmentRequestStatusEnum | null>(null);
    const {onRejectReasonDialogHandler} = useRejectRequestAction();
    const [comment, setComment] = useState('');
    const [dialog, closeDialog] = useDialog();

    const [equipmentRequestCreateHistoryRecord] = useEquipmentRequestCreateHistoryRecordMutation();

    const [statusOptions, setStatusOptions] = useState<EquipmentRequestStatusEnum[]>([
        EquipmentRequestStatusEnum.Submitted,
        EquipmentRequestStatusEnum.Pending,
        EquipmentRequestStatusEnum.DocumentPackageFormation,
        EquipmentRequestStatusEnum.ContractingServiceAndPlanningAndEconomicDepartmentApproval,
        EquipmentRequestStatusEnum.LotFormation,
        EquipmentRequestStatusEnum.Auction,
        EquipmentRequestStatusEnum.Acceptance,
        EquipmentRequestStatusEnum.EquipmentAccepted,
        EquipmentRequestStatusEnum.PassedToAno,
        EquipmentRequestStatusEnum.PassedToContractualService,
    ]);

    useEffect(() => {
        setStatus(equipmentRequestEntity?.requestStatus);
        if (isSingleRequest(equipmentRequestEntity)) {
            setStatusOptions(prev => Array.from(new Set([...prev, EquipmentRequestStatusEnum.Rejected])));
        }
        if (isCombinedRequest(equipmentRequestEntity)) {
            if (
                equipmentRequestEntity?.requestStatus === EquipmentRequestStatusEnum.Acceptance
                || equipmentRequestEntity?.requestStatus === EquipmentRequestStatusEnum.EquipmentAccepted
            ) {
                setStatusOptions([
                    EquipmentRequestStatusEnum.Acceptance,
                    EquipmentRequestStatusEnum.EquipmentAccepted
                ]);
            }
        }
    }, [equipmentRequestEntity]);

    const [updateCombinedEquipmentRequest] = useUpdateCombinedEquipmentRequestMutation({
        refetchQueries: ['getEquipmentRequests'],
    });

    const [updateEquipmentRequest] = useUpdateEquipmentRequestMutation({
        refetchQueries: ['getEquipmentRequests']
    });

    const [updateEquipmentRequestsStatuses] = useUpdateEquipmentRequestsStatusesMutation({
        refetchQueries: ['getEquipmentRequests']
    });


    const openEquipmentAcceptanceDialog = () => {
        dialog({
            onClose: closeDialog,
            title: "",
            maxWidth: "md",
            fullWidth: true,
            children: (
                <CombinedEquipmentAcceptanceDialog
                    combinedRequest={equipmentRequestEntity as CombinedEquipmentRequestEntity}
                    onClose={closeDialog}
                    loading={loading}
                    onAccept={(acceptanceCertificateId, acceptedEquipmentRequests) => {
                        setLoading(true);
                        const combinedRequest = equipmentRequestEntity as CombinedEquipmentRequestEntity;
                        updateCombinedEquipmentRequest({
                            variables: {
                                input: {
                                    id: equipmentRequestEntity.id,
                                    requestStatus: EquipmentRequestStatusEnum.EquipmentAccepted,
                                    acceptanceCertificateId,
                                    acceptedEquipmentRequests,
                                    serviceNoteScanFileId: combinedRequest.serviceNoteScanFileId || null,
                                }
                            }
                        }).then(() => {
                            equipmentRequestCreateHistoryRecord({
                                variables: {
                                    input: {
                                        requestStatus: EquipmentRequestStatusEnum.EquipmentAccepted,
                                        combinedEquipmentRequestId: equipmentRequestEntity.id,
                                        message: "Оборудование принято с актом приемки"
                                    }
                                }
                            }).then(() => {
                                enqueueSnackbar('Оборудование успешно принято', { variant: 'success' });
                                closeDialog();
                                setStatus(EquipmentRequestStatusEnum.EquipmentAccepted);
                            }).finally(() => {
                                setLoading(false);
                            });
                        }).catch(() => {
                            enqueueSnackbar('Ошибка при принятии оборудования', { variant: 'error' });
                            setLoading(false);
                        });
                    }}
                />
            )
        });
    };

    const openSingleEquipmentAcceptanceDialog = () => {
        dialog({
            onClose: closeDialog,
            title: "",
            maxWidth: "sm",
            fullWidth: true,
            children: (
                <SingleEquipmentAcceptanceDialog
                    equipmentRequest={equipmentRequestEntity as EquipmentRequestEntity}
                    onClose={closeDialog}
                    loading={loading}
                    onAccept={(acceptanceCertificateId, quantity, name) => {
                        setLoading(true);
                        
                        const acceptedEquipmentRequests = [{
                            equipmentRequestId: equipmentRequestEntity.id,
                            quantity,
                            ...(name && { name })
                        }];

                        updateEquipmentRequestsStatuses({
                            variables: {
                                input: {
                                    requestIds: [equipmentRequestEntity.id],
                                    newStatus: EquipmentRequestStatusEnum.EquipmentAccepted,
                                    acceptanceCertificateId,
                                    acceptedEquipmentRequests
                                }
                            }
                        }).then(() => {
                            equipmentRequestCreateHistoryRecord({
                                variables: {
                                    input: {
                                        requestStatus: EquipmentRequestStatusEnum.EquipmentAccepted,
                                        equipmentRequestId: equipmentRequestEntity.id,
                                        message: "Оборудование принято с актом приемки"
                                    }
                                }
                            }).then(() => {
                                enqueueSnackbar('Оборудование успешно принято', { variant: 'success' });
                                closeDialog();
                                setStatus(EquipmentRequestStatusEnum.EquipmentAccepted);
                            }).finally(() => {
                                setLoading(false);
                            });
                        }).catch(() => {
                            enqueueSnackbar('Ошибка при принятии оборудования', { variant: 'error' });
                            setLoading(false);
                        });
                    }}
                />
            )
        });
    };

    const confirmStatusChange = (newStatus: EquipmentRequestStatusEnum) => {
        setLoading(true)

        const updateVariables = {
            variables: {
                input: {
                    id: equipmentRequestEntity.id,
                    requestStatus: newStatus,
                },
            },
        };

        if (isCombinedRequest(equipmentRequestEntity)) {
            const combinedRequest = equipmentRequestEntity as CombinedEquipmentRequestEntity;
            updateCombinedEquipmentRequest({
                ...updateVariables,
                variables: {
                    ...updateVariables.variables,
                    input: {
                        ...updateVariables.variables.input,
                        serviceNoteScanFileId: combinedRequest.serviceNoteScanFileId || null,
                        acceptanceCertificateId: combinedRequest.acceptanceCertificateId || null,
                    },
                },
            }).then(() => {
                equipmentRequestCreateHistoryRecord({
                    variables: {
                        input: {
                            requestStatus: newStatus,
                            combinedEquipmentRequestId: equipmentRequestEntity.id,
                            message: comment
                        }
                    }
                }).then(() => {
                    setStatus(newStatus);
                    setComment('');
                    setOpenWarning(false)
                }).finally(()=>{
                    setLoading(false)
                }).catch(() => {
                    setLoading(false)
                })
            }).catch(() => {
                setLoading(false)
            });
        } else if (isSingleRequest(equipmentRequestEntity)) {
            const singleRequest = equipmentRequestEntity as EquipmentRequestEntity;
            updateEquipmentRequest({
                ...updateVariables,
                variables: {
                    ...updateVariables.variables,
                    input: {
                        ...updateVariables.variables.input,
                        count: equipmentRequestEntity.count,
                        equipmentTypeId: equipmentRequestEntity.equipmentTypeObject.id,
                        name: equipmentRequestEntity.name,
                        sportObjectId: equipmentRequestEntity.sportObject.id,
                        serviceNoteScanId: singleRequest.serviceNoteScan?.id || null,
                    },
                },
            }).then(() => {
                equipmentRequestCreateHistoryRecord({
                    variables: {
                        input: {
                            requestStatus: newStatus,
                            equipmentRequestId: equipmentRequestEntity.id,
                            message: comment
                        }
                    }
                }).then(() => {
                    setStatus(newStatus);
                    setComment('');
                    setOpenWarning(false)
                }).finally(()=>{
                    setLoading(false)
                }).catch(() => {
                    setLoading(false)
                })
            }).catch(() => {
                setLoading(false)
            })
        }
    };

    const onChangeStatus = (newStatus: EquipmentRequestStatusEnum) => {
        if (newStatus === EquipmentRequestStatusEnum.EquipmentAccepted) {
            if (isCombinedRequest(equipmentRequestEntity)) {
                openEquipmentAcceptanceDialog();
            } else if (isSingleRequest(equipmentRequestEntity)) {
                openSingleEquipmentAcceptanceDialog();
            }
            return;
        }
        
        setNextStatus(newStatus);
        setOpenWarning(true);
    };

    const handleCloseWarning = (confirm: boolean) => {
        if (confirm && nextStatus) {
            confirmStatusChange(nextStatus);
        } else {
            setOpenWarning(false);
            setNextStatus(null);
            setComment('');
        }
    };

    const statuses = statusOptions.map((value) => ({
        label: getLabelStatusRequest(value),
        value: value
    }));

    const warningMessage = (() => {
        switch (nextStatus) {
            case EquipmentRequestStatusEnum.Rejected:
                return "Вы собираетесь отклонить заявку. Это действие нельзя будет отменить.";
            case EquipmentRequestStatusEnum.Acceptance:
                return <div>
                    {`Установка статуса `}
                    <strong>{getLabelStatusRequest(nextStatus)}</strong>{` является необратимым действием.`}
                    <br/>
                    {`Вернуться на предыдущие шаги будет невозможно.`}
                </div>;
            default:
                return <div>
                    Вы уверены, что хотите изменить статус заявки?<br/>
                    {`Текущий статус: `} <strong>{getLabelStatusRequest(status)}</strong>, {`новый статус: `}
                    <strong>{getLabelStatusRequest(nextStatus)}</strong>
                </div>;
        }
    })();

    return (
        <>
            <Dialog open={openWarning} onClose={() => handleCloseWarning(false)}>
                <DialogTitle>Изменение статуса заявки</DialogTitle>
                <DialogContent>
                    {
                        (nextStatus === EquipmentRequestStatusEnum.Rejected
                            || nextStatus === EquipmentRequestStatusEnum.Acceptance)
                            ? <Alert variant="standard" severity="error">
                                <AlertTitle>Внимание!</AlertTitle>
                                {warningMessage}
                            </Alert>
                            : <Alert variant="standard" severity="info">
                                {warningMessage}
                            </Alert>
                    }

                    <Box mt={2}>
                        <Typography variant={'body2'} mb={2}>
                            Для подтверждения изменения статуса заявки, при необходимости добавьте комментарий и нажмите
                            кнопку "Подтвердить".
                        </Typography>
                        <TextField
                            multiline
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            fullWidth
                            size={'small'}
                            label='Комментарий'
                            placeholder={`Например: Заявка согласована и передана дальше, для формирования пакета документов.`}
                            variant={"filled"}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: 12,
                                }
                            }}
                            InputLabelProps={{shrink: true}}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseWarning(false)} color="primary">Отмена</Button>
                    <LoadingButton loading={loading} onClick={() => handleCloseWarning(true)} color="secondary" disabled={loading}>Подтвердить</LoadingButton>
                </DialogActions>
            </Dialog>

            {(isCombinedRequest(equipmentRequestEntity) || (isSingleRequest(equipmentRequestEntity) && !equipmentRequestEntity.isInCombinedRequest)) &&
                <Box sx={{
                    width: '100%',
                    '& .MuiFilledInput-root': {transform: 'scale(.8)', transformOrigin: 'left center'}
                }}>
                    <StatusSelect
                        value={status}
                        onChange={({target: {value}}) => {
                            if (value === EquipmentRequestStatusEnum.Rejected) {
                                onRejectReasonDialogHandler(equipmentRequestEntity as EquipmentRequestEntity);
                            } else {
                                onChangeStatus(value as EquipmentRequestStatusEnum);
                            }
                        }}
                        disabled={
                            status === EquipmentRequestStatusEnum.Supplied
                            || status === EquipmentRequestStatusEnum.EquipmentAccepted
                            || status === EquipmentRequestStatusEnum.Rejected
                            || loading
                            || disableEdit
                        }
                        loading={loading}
                        statuses={statuses}
                    />
                </Box>
            }

            {(
                    isSingleRequest(equipmentRequestEntity)
                    && equipmentRequestEntity?.isInCombinedRequest
                    && (
                        equipmentRequestEntity.parentCombinedRequest?.requestStatus === EquipmentRequestStatusEnum.Acceptance
                        || equipmentRequestEntity.parentCombinedRequest?.requestStatus === EquipmentRequestStatusEnum.EquipmentAccepted
                    )
                    && equipmentRequestEntity?.requestStatus !== EquipmentRequestStatusEnum.EquipmentAccepted
                ) &&
                <Box sx={{
                    width: '100%',
                    '& .MuiFilledInput-root': {transform: 'scale(.8)', transformOrigin: 'left center'}
                }}>
                    <Button size="small" onClick={() => onSuppliedDialogHandler(equipmentRequestEntity)}>
                        Принять оборудование
                    </Button>
                </Box>
            }
        </>
    );
};
